import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import * as api from "api/index";
import { Dialog } from "@progress/kendo-react-dialogs";
import {
    NumericCell,
    useDisableNumericWheel,
} from "../../../DibursementPayment/DisbursementInputField";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import SubmitButton from "components/common/SubmitButton";

const UserTargets = (props) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const yearlyWheelRef = useDisableNumericWheel();

    const BillingTargetCell = React.useCallback((cellProps) => (
        <NumericCell {...cellProps} />
    ), []);

    const sumMonthValues = (list) =>
        (list || []).reduce((acc, item) => acc + (Number(item.value) || 0), 0);

    const [dataState, setData] = React.useState(props.data);
    const [yearlyTarget, setYearlyTarget] = React.useState(
        sumMonthValues(props.data)
    );

    const hasMonthValues = (dataState || []).some(
        (item) => Number(item.value) > 0
    );

    // Sync yearly field with the sum of month targets.
    React.useEffect(() => {
        setYearlyTarget(sumMonthValues(dataState));
    }, [dataState]);

    const handleYearlyBlur = () => {
        const allMonthsZero = dataState.every(
            (item) => !item.value || Number(item.value) === 0
        );

        if (allMonthsZero && Number(yearlyTarget) > 0) {
            // Spread yearly target across months, remainder on the first month.
            const numMonths = dataState.length;
            const base = Math.floor((Number(yearlyTarget) / numMonths) * 100) / 100;
            const remainder =
                Math.round((Number(yearlyTarget) - base * numMonths) * 100) / 100;
            const newData = dataState.map((item, index) => ({
                ...item,
                value: index === 0 ? Math.round((base + remainder) * 100) / 100 : base,
            }));
            setData(newData);
        } else {
            // Months already set: just reflect the total.
            setYearlyTarget(sumMonthValues(dataState));
        }
    };

    const handleClearMonthTargets = () => {
        const newData = dataState.map((item) => ({ ...item, value: 0 }));
        setData(newData);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        let data = [];
        dataState.map((item_1) => {
            let itemToPost = {};
            let dataTemp = {};
            dataTemp.active = true;
            dataTemp.branchId = null;
            dataTemp.departmentId = null;
            dataTemp.value = item_1.value;
            // Send only the date part (YYYY-MM-DD), not the time.
            itemToPost.month = item_1.originalMonth
                ? String(item_1.originalMonth).split("T")[0]
                : item_1.originalMonth;
            itemToPost.targetValues = [dataTemp];
            data.push(itemToPost);
        });
        try {
            await api.editBillingTargets(props.item.userID, data);
            if (props.onRefresh) {
                await props.onRefresh();
            }
        } catch (error) {
            return;
        } finally {
            setIsSubmitting(false);
        }

        props.cancelEdit();
    };

    const itemChange = (event) => {
        const newData = dataState.map((item) => {
            if (item.counter === event.dataItem.counter) {
                return { ...item, [event.field]: event.value };
            }
            return item;
        });
        setData(newData);
    };

    return (
        <Dialog
            onClose={props.cancelEdit}
            className="dialog-lg h-100"
            title={`Billing Target - ${props.item?.displayname} (${props.item?.userRef})`}
        >
            <div className="d-flex flex-column h-100 w-100">
                <div
                    className="flex-grow-1 d-flex flex-column overflow-hidden px-4 py-3 mx-auto w-100"
                    style={{ maxWidth: "80%", minHeight: 0 }}
                >
                    <p className="fs-md fw-semibold mb-2">Monthly Targets</p>
                    <div className="flex-grow-1 d-flex" style={{ minHeight: 0 }}>
                        <Grid
                            data={dataState}
                            onItemChange={itemChange}
                            rowHeight="10"
                            style={{
                                margin: "auto",
                                height: "100%",
                                width: "100%",
                                overflow: "auto",
                            }}
                        >
                            <Column field="month" title="Month" className="ps-2" />
                            <Column field="value" title="Target" cell={BillingTargetCell} width="200" headerClassName="grid-header-center" />
                        </Grid>
                    </div>
                    <div className="d-flex justify-content-end mb-3 mt-3">
                        <button
                            type="button"
                            className="button-small bgBtnSecondary border-white d-inline-flex align-items-center justify-content-center"
                            style={{ paddingTop: 0, paddingBottom: 0 }}
                            onClick={handleClearMonthTargets}
                            disabled={!hasMonthValues}
                        >
                            Clear
                        </button>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            flexDirection: "column",
                        }}
                    >
                        <div className="right-align d-flex align-items-center justify-content-end">
                            <div className="pe-3">
                                <p className="fs-md fw-semibold mb-0">Total Yearly Target</p>
                            </div>
                            <NumericTextBox
                                ref={yearlyWheelRef}
                                width={180}
                                style={{ textAlign: "right" }}
                                value={yearlyTarget}
                                onChange={(e) => setYearlyTarget(e.value ?? 0)}
                                onBlur={handleYearlyBlur}
                                placeholder="0.00"
                                spinners={false}
                                format="n2"
                            />
                        </div>
                    </div>
                </div>

                <div className="dialog-footer flex-shrink-0">
                    <button
                        className="common-btn bgBtnSecondary border-white me-3"
                        onClick={props.cancelEdit}
                        type="button"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <SubmitButton
                        className="k-button common-btn me-4"
                        type="submit"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                    >
                        Save
                    </SubmitButton>
                </div>
            </div>
        </Dialog>
    );
};
export default UserTargets;
